import * as React from 'react';

import {
  Badge,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from '@/components/cargofl-ui';
import { PILL_CLASS } from '@/lib/badges';
import {
  useCreateSkill,
  useEmployeeSkills,
  useSetEmployeeSkill,
  useSkills,
} from '@/lib/talent-api';

/** 0-5, the scale the API validates against. */
const LEVELS = [0, 1, 2, 3, 4, 5];

/**
 * Skill levels for one person: what they hold, and what the role needs.
 *
 * Both numbers are set here rather than the required level living on the
 * designation, because the same title needs different things in different teams -
 * and a required level nobody can edit from the app is one nobody keeps current.
 * The gap is never stored; the API derives it on read.
 */
export function SkillsDialog({
  employee,
  onOpenChange,
}: {
  employee: { id: string; name: string } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: catalog, isPending } = useSkills();
  const { data: held } = useEmployeeSkills(employee?.id);
  const setSkill = useSetEmployeeSkill();
  const createSkill = useCreateSkill();

  const levels = React.useMemo(
    () => new Map((held ?? []).map((s) => [s.skillId, s])),
    [held],
  );

  async function save(skillId: string, next: { currentLevel: number; requiredLevel: number }) {
    if (!employee) return;
    try {
      await setSkill.mutateAsync({ employeeId: employee.id, skillId, ...next });
    } catch (e) {
      toast.error('Could not save that level', {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  /**
   * Put a skill on this person, whether or not the company had it yet.
   *
   * The catalogue is shared, so the common case is picking one that exists — and the
   * old field could only add. Typing "Freight pricing" when three people already hold
   * it made a fourth row nobody could tell from the others, and the skill-gap report
   * counted them separately.
   *
   * A picked skill is set to level 0/0 rather than merely highlighted: the list below
   * shows the whole catalogue, so "added" has to mean something happened to this
   * employee's row, not just that the name now exists somewhere.
   */
  async function addSkill(skillId: string, skillName: string) {
    if (!employee) return;
    if (levels.has(skillId)) {
      toast.info(`${skillName} is already on ${employee.name}`);
      return;
    }
    await setSkill.mutateAsync({
      employeeId: employee.id,
      skillId,
      currentLevel: 0,
      requiredLevel: 0,
    });
    toast.success(`${skillName} added — set the levels below`);
  }

  async function createAndAdd(name: string) {
    // The API upserts on (organization, name), so a race with another HR user lands
    // on the same row rather than a second one.
    const made = await createSkill.mutateAsync({ name });
    await addSkill(made.id, made.name);
  }

  return (
    <Dialog open={employee !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Skills — {employee?.name}</DialogTitle>
          <DialogDescription>
            Level 0–5. Where the required level is above the current one, the difference shows as a
            gap on the analytics dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="new-skill">Add a skill</Label>
          <Combobox
            id="new-skill"
            value={null}
            placeholder="Find a skill, or type a new one"
            searchPlaceholder="e.g. Negotiation, React, Freight pricing"
            emptyText="No skill matches"
            options={(catalog ?? []).map((skill) => ({
              value: skill.id,
              label: skill.name,
              hint: levels.has(skill.id) ? 'already added' : undefined,
              description: skill.category ?? undefined,
            }))}
            onChange={(id) => {
              const skill = (catalog ?? []).find((s) => s.id === id);
              if (skill) void addSkill(skill.id, skill.name);
            }}
            onCreate={createAndAdd}
            createLabel="Create skill"
          />
        </div>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {isPending && <Skeleton className="h-24 w-full" />}

          {!isPending && (catalog ?? []).length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No skills defined yet. Add the first one above — the list is shared by everyone.
            </p>
          )}

          {(catalog ?? []).map((skill) => {
            const row = levels.get(skill.id);
            const current = row?.currentLevel ?? 0;
            const required = row?.requiredLevel ?? 0;
            const gap = Math.max(0, required - current);

            return (
              <div key={skill.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{skill.name}</p>
                  {skill.category && (
                    <p className="truncate text-xs text-muted-foreground">{skill.category}</p>
                  )}
                </div>

                {gap > 0 && (
                  <Badge variant="soft-warning" className={PILL_CLASS}>
                    gap {gap}
                  </Badge>
                )}

                <LevelSelect
                  label={`Current level for ${skill.name}`}
                  value={current}
                  onChange={(v) => void save(skill.id, { currentLevel: v, requiredLevel: required })}
                />
                <span className="text-xs text-muted-foreground">of</span>
                <LevelSelect
                  label={`Required level for ${skill.name}`}
                  value={required}
                  onChange={(v) => void save(skill.id, { currentLevel: current, requiredLevel: v })}
                />
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LevelSelect({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-8 w-16" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LEVELS.map((level) => (
          <SelectItem key={level} value={String(level)}>
            {level}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
